import { Injectable, Optional } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { PwaService } from './pwa.service';

interface BeforeInstallPromptEvent extends Event {
    prompt: () => Promise<void>;
    userChoice: Promise<{ outcome?: 'accepted' | 'dismissed'; platform?: string[] | string }>;
}

@Injectable({ providedIn: 'root' })
export class PwaInstallPromptService {
    private deferredPrompt: BeforeInstallPromptEvent | null = null;
    private readonly installAvailableSubject = new BehaviorSubject<boolean>(false);
    private readonly installedSubject = new BehaviorSubject<boolean>(false);
    private readonly manualInstallOnlySubject = new BehaviorSubject<boolean>(false);

    constructor(
        private readonly translateService: TranslateService,
        @Optional() private readonly pwaService?: PwaService
    ) {
        if (!this.isBrowser()) {
            return;
        }

        this.evaluateInitialState();
        window.addEventListener('beforeinstallprompt', this.handleBeforeInstallPrompt, { once: false });
        window.addEventListener('appinstalled', this.handleAppInstalled);
    }

    get installAvailable$(): Observable<boolean> {
        return this.installAvailableSubject.asObservable();
    }

    get installed$(): Observable<boolean> {
        return this.installedSubject.asObservable();
    }

    get manualInstallOnly$(): Observable<boolean> {
        return this.manualInstallOnlySubject.asObservable();
    }

    get isInstallAvailable(): boolean {
        return this.installAvailableSubject.value;
    }

    get isInstalled(): boolean {
        return this.installedSubject.value;
    }

    get isManualInstallOnly(): boolean {
        return this.manualInstallOnlySubject.value;
    }

    getInstallGuideKey(): string {
        if (!this.isBrowser()) {
            return 'HEADER.INSTALL_GUIDE.DEFAULT';
        }
        return this.resolveInstructionKey();
    }

    async attemptInstall(): Promise<boolean> {
        if (!this.isBrowser()) {
            return false;
        }

        if (!this.deferredPrompt) {
            this.showManualInstructions();
            return false;
        }

        try {
            this.installAvailableSubject.next(false);
            await this.deferredPrompt.prompt();
            const choice = await this.deferredPrompt.userChoice;
            const accepted = choice?.outcome === 'accepted';
            if (accepted) {
                this.installedSubject.next(true);
                this.manualInstallOnlySubject.next(false);
            }
            this.deferredPrompt = null;
            if (!accepted) {
                this.evaluateInstallAvailability();
            }
            return accepted;
        } catch (error) {
            console.error('Error while prompting PWA install:', error);
            this.deferredPrompt = null;
            this.evaluateInstallAvailability();
            return false;
        }
    }

    private readonly handleBeforeInstallPrompt = (event: Event): void => {
        try {
            event.preventDefault();
            this.deferredPrompt = event as BeforeInstallPromptEvent;
            this.installAvailableSubject.next(true);
            this.manualInstallOnlySubject.next(false);
        } catch (error) {
            console.error('Error handling beforeinstallprompt event:', error);
        }
    };

    private readonly handleAppInstalled = (): void => {
        try {
            this.deferredPrompt = null;
            this.installedSubject.next(true);
            this.manualInstallOnlySubject.next(false);
            this.installAvailableSubject.next(false);
        } catch (error) {
            console.error('Error handling appinstalled event:', error);
        }
    };

    private evaluateInitialState(): void {
        try {
            const alreadyInstalled = this.pwaService?.isPwaMode?.() ?? this.detectStandaloneMode();
            if (alreadyInstalled) {
                this.installedSubject.next(true);
                this.installAvailableSubject.next(false);
                this.manualInstallOnlySubject.next(false);
            } else {
                this.evaluateInstallAvailability();
            }
        } catch (error) {
            console.error('Error evaluating initial PWA install state:', error);
        }
    }

    private evaluateInstallAvailability(): void {
        try {
            const canPrompt = !!this.deferredPrompt;
            this.installAvailableSubject.next(canPrompt);
            this.manualInstallOnlySubject.next(!canPrompt && this.shouldOfferManualInstall());
        } catch (error) {
            console.error('Error evaluating PWA install availability:', error);
        }
    }

    private detectStandaloneMode(): boolean {
        try {
            const displayModeStandalone = window.matchMedia('(display-mode: standalone)').matches;
            const displayModeFullscreen = window.matchMedia('(display-mode: fullscreen)').matches;
            const navigatorStandalone = (navigator as Navigator & { standalone?: boolean }).standalone === true;
            return displayModeStandalone || displayModeFullscreen || navigatorStandalone;
        } catch (error) {
            console.error('Error detecting standalone mode:', error);
            return false;
        }
    }

    private showManualInstructions(): void {
        try {
            const messageKey = this.resolveInstructionKey();
            const message = this.translateService.instant(messageKey).replace(/<[^>]+>/g, ' ');
            window.alert(message);
        } catch (error) {
            console.error('Error showing manual PWA install instructions:', error);
        }
    }

    private resolveInstructionKey(): string {
        const userAgent = navigator.userAgent.toLowerCase();
        if (this.isChromiumFamily(userAgent)) {
            return 'HEADER.INSTALL_GUIDE.CHROMIUM';
        }
        if (this.isSafari(userAgent)) {
            return 'HEADER.INSTALL_GUIDE.SAFARI';
        }
        if (userAgent.includes('firefox')) {
            return 'HEADER.INSTALL_GUIDE.FIREFOX';
        }
        return 'HEADER.INSTALL_GUIDE.DEFAULT';
    }

    private isBrowser(): boolean {
        return typeof window !== 'undefined' && typeof navigator !== 'undefined';
    }

    private shouldOfferManualInstall(): boolean {
        if (!this.isBrowser()) {
            return false;
        }

        return !this.isInstalled;
    }

    private isChromiumFamily(userAgent: string): boolean {
        const hasWebkit = userAgent.includes('applewebkit');
        const hasChromeToken = userAgent.includes('chrome');
        const hasEdgeToken = userAgent.includes('edg');
        const hasOperaToken = userAgent.includes('opr');
        if (!hasWebkit && !hasEdgeToken) {
            return false;
        }
        return hasChromeToken || hasEdgeToken || hasOperaToken;
    }

    private isSafari(userAgent: string): boolean {
        const isSafari = userAgent.includes('safari') && !userAgent.includes('chrome') && !userAgent.includes('crios') && !userAgent.includes('opr') && !userAgent.includes('fxios');
        return isSafari;
    }
}
